import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/i18n/LanguageContext";
import iLoveSerumImg from "@/assets/i-love-serum.png";
import dermikaGoldImg from "@/assets/products/dermika-gold-24k-cream.jpg";
import bielendaVitCImg from "@/assets/products/bielenda-vitc-serum.jpg";
import bielendaSpf50Img from "@/assets/products/bielenda-spf50.jpg";

const BUCKET = "product-images";

interface Product {
  id: string;
  title: string;
  description: string;
  image: string | null;
  slug: string;
  price: number;
  salePrice: number;
  brand: string;
}

const products: Product[] = [
  {
    id: "1",
    title: "I Love Serum",
    description: "Brightening vitamin C serum for luminous, glowing skin.",
    image: iLoveSerumImg,
    slug: "i-love-serum",
    price: 55,
    salePrice: 40,
    brand: "Soraya",
  },
  {
    id: "2",
    title: "Gold 24K Total Cream",
    description: "Luxury anti-aging cream with 24K gold for youthful radiance.",
    image: dermikaGoldImg,
    slug: "dermika-gold-24k-cream",
    price: 100,
    salePrice: 70,
    brand: "Dermika",
  },
  {
    id: "3",
    title: "Energy Boost 5% Vitamin C Serum",
    description: "Concentrated serum with 5% Tetra-Vit C for brightening & protection.",
    image: bielendaVitCImg,
    slug: "bielenda-energy-boost-vitc-serum",
    price: 120,
    salePrice: 84,
    brand: "Bielenda",
  },
  {
    id: "4",
    title: "Satin Protective SPF 50",
    description: "Lightweight, non-greasy sunscreen for all-day UV protection.",
    image: bielendaSpf50Img,
    slug: "bielenda-spf50-cream",
    price: 120,
    salePrice: 84,
    brand: "Bielenda",
  },
];

const productDescriptionsAr: Record<string, string> = {
  "i-love-serum": "سيروم تفتيح بفيتامين سي لبشرة مشرقة ومتألّقة.",
  "dermika-gold-24k-cream": "كريم فاخر مضاد للشيخوخة بذهب 24 قيراط لإشراقة شبابية.",
  "bielenda-energy-boost-vitc-serum": "سيروم مركّز بـ 5% Tetra-Vit C للتفتيح والحماية.",
  "bielenda-spf50-cream": "واقي شمس خفيف وغير دهني لحماية طوال اليوم من الأشعة فوق البنفسجية.",
};

const ProductCard = ({ product, isMobile }: { product: Product; isMobile: boolean }) => {
  const [hovered, setHovered] = useState(false);
  const showButtons = isMobile || hovered;
  const { t, lang } = useLanguage();
  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  return (
    <Link
      to={`/${product.slug}/purchase`}
      className="group relative cursor-pointer block"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Glow aura */}
      <div
        className="pointer-events-none absolute -inset-3 rounded-3xl opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: "radial-gradient(ellipse at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(232,140,164,0.45) 0%, transparent 70%)",
        }}
      />

      <div
        className="relative overflow-hidden rounded-2xl bg-white shadow-md transition-all duration-500 group-hover:shadow-xl"
        style={{ aspectRatio: "4/5" }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          e.currentTarget.parentElement?.style.setProperty("--mouse-x", `${x}%`);
          e.currentTarget.parentElement?.style.setProperty("--mouse-y", `${y}%`);
        }}
      >
        {product.image ? (
          <img
            src={product.image}
            alt={product.title}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            draggable={false}
          />
        ) : (
          <div className="h-full w-full animate-pulse bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200" />
        )}

        {/* Bottom gradient overlay */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Text */}
        <div className="absolute inset-x-0 bottom-0 p-2.5 sm:p-5">
          <span
            className="inline-block rounded-full px-2 py-0.5 text-[7px] sm:text-[9px] font-bold uppercase tracking-wider mb-0.5 sm:mb-1"
            style={{
              fontFamily: "'Nunito', sans-serif",
              background: "rgba(255,255,255,0.15)",
              color: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(4px)",
            }}
          >
            {product.brand}
          </span>
          <h3
            className="text-[11px] sm:text-lg font-bold text-white leading-tight line-clamp-2"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {product.title}
          </h3>
          <p
            className="mt-0.5 text-[9px] sm:text-sm text-white/70 leading-snug line-clamp-1 sm:line-clamp-2"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {lang === "ar" ? (productDescriptionsAr[product.slug] || product.description) : product.description}
          </p>

          {/* Buttons — always visible on mobile */}
          <div
            className="mt-1.5 sm:mt-4 flex gap-1 sm:gap-2.5 transition-all duration-300"
            style={{
              opacity: showButtons ? 1 : 0,
              transform: showButtons ? "translateY(0)" : "translateY(8px)",
            }}
          >
            <Link
              to={`/${product.slug}/purchase`}
              className="rounded-full px-2.5 sm:px-5 py-1 sm:py-2 text-[9px] sm:text-xs font-bold text-white transition-all duration-200 active:scale-[0.97]"
              style={{
                fontFamily,
                background: "linear-gradient(180deg, rgba(232,140,164,0.9) 0%, rgba(210,110,140,0.85) 100%)",
                boxShadow: "inset 0 1px 3px rgba(255,255,255,0.5), 0 4px 12px rgba(232,140,164,0.3)",
              }}
            >
              {t("topProducts.buyNow")}
            </Link>
            <Link
              to={`/${product.slug}/info`}
              className="rounded-full border border-white/30 bg-white/10 px-2.5 sm:px-5 py-1 sm:py-2 text-[9px] sm:text-xs font-semibold text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/20 active:scale-[0.97]"
              style={{ fontFamily }}
            >
              {t("topProducts.moreInfo")}
            </Link>
          </div>
        </div>
      </div>
    </Link>
  );
};

const TopProducts = () => {
  const isMobile = useIsMobile();
  const { t, lang } = useLanguage();
  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";
  const [uploadedImages, setUploadedImages] = useState<Record<string, string>>({});
  const [checkedSlugs, setCheckedSlugs] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchForProduct = async (slug: string) => {
      try {
        const { data } = await supabase.storage.from(BUCKET).list(slug, {
          sortBy: { column: "name", order: "asc" },
        });
        if (data && data.length > 0) {
          const first = data.find((f) => !f.name.startsWith(".") && !f.name.startsWith("__"));
          if (first) {
            const { data: urlData } = supabase.storage
              .from(BUCKET)
              .getPublicUrl(`${slug}/${first.name}`);
            setUploadedImages((prev) => ({ ...prev, [slug]: urlData.publicUrl }));
          }
        }
      } catch {
        // silently skip
      }
      setCheckedSlugs((prev) => new Set(prev).add(slug));
    };
    products.forEach((p) => fetchForProduct(p.slug));
  }, []);

  return (
    <section id="top-products" className="bg-white px-4 sm:px-6 py-12 sm:py-20 lg:px-16">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8 sm:mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-0">
          <h2
            className="text-2xl sm:text-4xl font-bold overflow-visible"
            style={{
              fontFamily: "'Poppins', sans-serif",
              background: "linear-gradient(90deg, #E88CA4 0%, #a8405c 30%, #f8c8d8 60%, #E88CA4 100%)",
              backgroundSize: "300% 100%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              WebkitTextFillColor: "transparent",
              animation: "gradientShift 4s ease infinite",
              lineHeight: 1.4,
              paddingTop: "0.15em",
            }}
          >
            {t("topProducts.title")}
          </h2>
          <Link
            to="/shop"
            className="group flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200"
            style={{
              fontFamily,
              color: "#E88CA4",
            }}
          >
            {t("topProducts.seeAll")}
            <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">
              →
            </span>
          </Link>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
          {products.map((product) => {
            const checked = checkedSlugs.has(product.slug);
            const uploadedUrl = uploadedImages[product.slug];
            const imageUrl = !checked ? null : (uploadedUrl || product.image);
            return (
              <ProductCard
                key={product.id}
                product={{
                  ...product,
                  image: imageUrl,
                }}
                isMobile={isMobile}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TopProducts;
